import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ShieldCheck, AlertTriangle, XCircle, ArrowRight,
  CheckCircle2, Clock, RefreshCw, FileWarning, Receipt,
} from "lucide-react";
import { getRiskGradient } from "@/lib/color-intelligence";

interface ConfidenceData {
  score: number;
  status: "green" | "amber" | "red";
  breakdown: {
    reconMatchRate: number;
    pendingApprovals: number;
    unresolvedExceptions: number;
    syncErrors: number;
    overdueInvoices: number;
    overdueBills: number;
  };
  items: { label: string; count: number; href: string }[];
}

const LABEL_ICONS: Record<string, typeof Clock> = {
  pendingApprovals: Clock,
  unresolvedExceptions: FileWarning,
  syncErrors: RefreshCw,
  overdueInvoices: Receipt,
  overdueBills: Receipt,
  unmatchedPayments: AlertTriangle,
};

function getItemRiskLevel(label: string, count: number): string {
  if (count === 0) return "text-muted-foreground";
  if (label === "syncErrors" || label === "unresolvedExceptions") {
    return count >= 5 ? "text-red-500 dark:text-red-400" : count >= 2 ? "text-orange-500 dark:text-orange-400" : "text-amber-500 dark:text-amber-400";
  }
  if (label === "overdueInvoices" || label === "overdueBills") {
    return count >= 10 ? "text-red-500 dark:text-red-400" : count >= 5 ? "text-orange-500 dark:text-orange-400" : "text-amber-500 dark:text-amber-400";
  }
  return count >= 5 ? "text-orange-500 dark:text-orange-400" : "text-muted-foreground";
}

export function ConfidenceIndicator() {
  const { t } = useTranslation("dashboard");
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);

  const { data } = useQuery<ConfidenceData>({
    queryKey: ["/api/confidence"],
    refetchInterval: 60000,
  });

  if (!data) return null;

  const risk = getRiskGradient(data.score);
  const StatusIcon = risk.level === "low" || risk.level === "moderate" ? ShieldCheck : risk.level === "elevated" ? AlertTriangle : XCircle;
  const statusLabel = risk.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-all hover-elevate"
              data-testid="button-confidence-indicator"
              aria-label={t("confidence.title")}
            >
              <div className="relative">
                <ShieldCheck className="w-4 h-4" />
                <span
                  className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${risk.dotClassName} ring-2 ring-background`}
                  data-testid="badge-confidence-status"
                />
              </div>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {t("confidence.title")} — {statusLabel}
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-80 p-0" data-testid="panel-confidence">
        <div className={`px-4 py-3 border-b ${risk.bgClassName}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${risk.textClassName}`} />
              <span className="text-sm font-semibold" data-testid="text-confidence-title">
                {t("confidence.title")}
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${risk.textClassName}`}
              data-testid="text-confidence-score"
            >
              <span className={`w-2 h-2 rounded-full ${risk.dotClassName}`} />
              {statusLabel}
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-background/50 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${risk.className}`} style={{ width: `${data.score}%` }} />
          </div>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500 mb-2" />
              <p className="text-sm text-muted-foreground" data-testid="text-confidence-all-good">
                {t("confidence.allGood")}
              </p>
            </div>
          ) : (
            <div className="py-1">
              <div className="px-4 py-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  {t("confidence.degradingScore")}
                </span>
              </div>
              {data.items.map((item) => {
                const Icon = LABEL_ICONS[item.label] || AlertTriangle;
                const itemColor = getItemRiskLevel(item.label, item.count);
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      setLocation(item.href);
                      setOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover-elevate transition-colors flex items-center gap-3"
                    data-testid={`confidence-item-${item.label}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${itemColor}`} />
                    <span className="flex-1 text-sm">
                      {t(`confidence.${item.label}`, { count: item.count })}
                    </span>
                    <span className={`text-xs font-mono tabular-nums ${itemColor}`}>{item.count}</span>
                    <ArrowRight className="w-3 h-3 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
